import { Tooltip } from "@coval/ui"
import { Archive, Star } from "lucide-react"

import type { TargetCondition } from "@/types/metrics/contracts"

import type { CriterionResult } from "../../types"
import { CriteriaDetailList } from "./CriteriaDetailList"
import { LoadingIndicator } from "./LoadingIndicator"
import { ReviewBadge } from "./ReviewBadge"

interface HumanReviewAnnotationCardProps {
  displayValue: string | null
  targetCondition?: TargetCondition
  emptyLabel?: string
  assignee?: string | null
  date?: string | null
  isPrimary: boolean
  canSetPrimary: boolean
  onSetPrimary?: () => void
  isSettingPrimary?: boolean
  onArchive?: () => void
  isArchiving?: boolean
  criteria?: CriterionResult[]
  conflictIndices?: Set<number>
  explanation?: string | null
  footer?: React.ReactNode
  isLoading?: boolean
  errorStatus?: string
  errorMessage?: string
}

export const HumanReviewAnnotationCard: React.FC<
  HumanReviewAnnotationCardProps
> = ({
  displayValue,
  targetCondition,
  emptyLabel,
  assignee,
  date,
  isPrimary,
  canSetPrimary,
  onSetPrimary,
  isSettingPrimary = false,
  onArchive,
  isArchiving = false,
  criteria = [],
  conflictIndices,
  explanation,
  footer,
  isLoading = false,
  errorStatus,
  errorMessage,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-start">
        <LoadingIndicator />
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-1 group">
      <ReviewBadge
        value={displayValue}
        targetCondition={targetCondition}
        commentOnly={!displayValue}
        emptyLabel={emptyLabel}
        errorStatus={errorStatus}
        errorMessage={errorMessage}
      />
      <div className="flex items-start justify-between gap-1">
        <div className="flex flex-col min-w-0">
          {assignee && (
            <span className="text-xs text-coval-text-muted break-all">
              {assignee}
            </span>
          )}
          {date && (
            <span className="text-xs text-coval-text-muted">{date}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {canSetPrimary && (
            <Tooltip
              content={isPrimary ? "Counted in stats" : "Set as primary"}
              side="top"
            >
              <button
                type="button"
                onClick={() => {
                  if (!isPrimary) {
                    onSetPrimary?.()
                  }
                }}
                disabled={isPrimary || isSettingPrimary}
                className={`transition-all ${
                  isPrimary
                    ? "text-coval-warning-text"
                    : "text-coval-text-muted opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-coval-warning-text focus-visible:ring-2 focus-visible:ring-coval-warning-border focus-visible:outline-none"
                } ${isSettingPrimary ? "opacity-50" : ""}`}
                aria-label={isPrimary ? "Counted in stats" : "Set as primary"}
              >
                <Star
                  className="w-3 h-3"
                  fill={isPrimary ? "currentColor" : "none"}
                />
              </button>
            </Tooltip>
          )}
          {onArchive && (
            <Tooltip content="Archive annotation" side="top">
              <button
                type="button"
                onClick={onArchive}
                disabled={isArchiving}
                className="text-coval-text-muted opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-coval-danger-text focus-visible:ring-2 focus-visible:ring-coval-danger-border focus-visible:outline-none transition-all disabled:opacity-50"
                aria-label="Archive annotation"
              >
                <Archive className="w-3 h-3" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      {criteria.length > 0 && (
        <CriteriaDetailList
          criteria={criteria}
          conflictIndices={conflictIndices}
        />
      )}
      {explanation && (
        <p className="text-sm text-foreground break-words">{explanation}</p>
      )}
      {footer}
    </div>
  )
}
